import {
    DatabaseType,
    FilterObject,
    InputDatasetStyles,
    RequestOptions,
    ShapeStyle,
} from "@biggeo/bg-server-lib/studio";
import { getRandomColor, WithPartialValues } from "@biggeo/bg-utils";
import * as A from "fp-ts/lib/Array";
import { pipe } from "fp-ts/lib/function";
import isEmpty from "lodash/isEmpty";
import isEqual from "lodash/isEqual";
import { useMemo } from "react";
import { MapContextDataset, MapContextDatasetConfiguration } from "../types";
import { useMap } from "../map";
import { DEFAULT_LOGIC_OPERATOR } from "../../../filter-criteria/utils/utils";
import { levelSetPercentiles } from "../../../../constants/map.constants";
import { getMultiFiltersType } from "./map-utils";

export type SetDatasetContextType = {
    addDatasets: (values: MapContextDataset | MapContextDataset[]) => void;
    updateDataset: (values: {
        dataSourceId: string;
        dataset: Partial<WithPartialValues<MapContextDataset>>;
    }) => void;
    removeDataset: (values: {
        dataSourceId: string;
        mapTemplateDatasetId?: string;
    }) => void;
    overrideDatasets: (values: MapContextDataset[]) => void;
    reorderDatasets: (dataSourceIds: string[]) => void;
};

export const setDatasetContext = (): SetDatasetContextType => {
    const { dispatch } = useMap();

    const addDatasets = (values: MapContextDataset | MapContextDataset[]) => {
        dispatch?.({ type: "ADD_DATASETS", values });
    };

    const updateDataset = (values: {
        dataSourceId: string;
        dataset: Partial<WithPartialValues<MapContextDataset>>;
    }) => {
        dispatch?.({ type: "UPDATE_DATASET", values });
    };

    const removeDataset = (values: {
        dataSourceId: string;
        mapTemplateDatasetId?: string;
    }) => {
        dispatch?.({ type: "REMOVE_DATASET", values });
    };

    const overrideDatasets = (values: MapContextDataset[]) => {
        dispatch?.({ type: "OVERRIDE_DATASETS", values });
    };

    const reorderDatasets = (dataSourceIds: string[]) => {
        dispatch?.({ type: "REORDER_DATASETS", values: { dataSourceIds } });
    };

    return {
        addDatasets,
        updateDataset,
        removeDataset,
        overrideDatasets,
        reorderDatasets,
    };
};

// ███╗   ███╗██╗   ██╗██╗  ████████╗██╗███████╗██╗██╗  ████████╗███████╗██████╗ ███████╗
// ████╗ ████║██║   ██║██║  ╚══██╔══╝██║██╔════╝██║██║  ╚══██╔══╝██╔════╝██╔══██╗██╔════╝
// ██╔████╔██║██║   ██║██║     ██║   ██║█████╗  ██║██║     ██║   █████╗  ██████╔╝███████╗
// ██║╚██╔╝██║██║   ██║██║     ██║   ██║██╔══╝  ██║██║     ██║   ██╔══╝  ██╔══██╗╚════██║
// ██║ ╚═╝ ██║╚██████╔╝███████╗██║   ██║██║     ██║███████╗██║   ███████╗██║  ██║███████║
// ╚═╝     ╚═╝ ╚═════╝ ╚══════╝╚═╝   ╚═╝╚═╝     ╚═╝╚══════╝╚═╝   ╚══════╝╚═╝  ╚═╝╚══════╝

export const getMultiFilters = (): FilterObject[] => {
    const { datasets } = useMap();

    return useMemo(() => getMultiFiltersType({ datasets }), [datasets]);
};

// ██████╗ ███████╗███████╗ █████╗ ██╗   ██╗██╗  ████████╗███████╗
// ██╔══██╗██╔════╝██╔════╝██╔══██╗██║   ██║██║  ╚══██╔══╝██╔════╝
// ██║  ██║█████╗  █████╗  ███████║██║   ██║██║     ██║   ███████╗
// ██║  ██║██╔══╝  ██╔══╝  ██╔══██║██║   ██║██║     ██║   ╚════██║
// ██████╔╝███████╗██║     ██║  ██║╚██████╔╝███████╗██║   ███████║
// ╚═════╝ ╚══════╝╚═╝     ╚═╝  ╚═╝ ╚═════╝ ╚══════╝╚═╝   ╚══════╝

export const getDefaultDatasetConfigurationOptions = (): RequestOptions => ({
    polygonMaxGeomsPerCell: 75,
    pointMaxGeomsPerCell: 101,
    polygonResolutionOffset: 3,
    pointResolutionOffset: 3,
    levelSets: levelSetPercentiles,
});

export const getDefaultDatasetConfiguration =
    (): MapContextDatasetConfiguration => ({
        isOpen: false,
        options: getDefaultDatasetConfigurationOptions(),
        showLevelSets: true,
        showPoints: true,
    });

export const getDefaultDatasetStyles = (): InputDatasetStyles => {
    const color = getRandomColor();

    return {
        shape: ShapeStyle.oval,
        fill: {
            color,
            opacity: 0.9,
        },
        stroke: {
            color,
            opacity: 0.9,
        },
        customMarker: null,
        dataAggregation: {
            heatmap: undefined,
        },
    };
};
